import { EditBoard } from 'Assets/icons/EditBoard'
import { FlexRow } from 'Providers/app.styled'
import React, { useState } from 'react'
import { CancelPlanModal } from '../Modals/CancelModal/CancelModal'
import { ActionText, ActionDescription, ErrorText } from '../PlanDetails/PlanDetails.styled'
import { useTranslation, Trans } from 'react-i18next'
import { CancelPlanRequestProps, useCancelPlan } from '../../api/cancelPlan'
import { useGetErrorText } from 'Utils/errorText'
import { ConfirmationPlanCancelModal } from '../Modals/ConfirmationPlanCancelModal'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { ExtendTrialModal } from '../Modals/ExtendTrialModal'
import { useExtendTrial } from 'Features/accountSettings/api/extendTrial'
import { queryClient } from 'Lib/react-query'
import { format } from 'date-fns'
import { useRevokeCancelation } from 'Features/accountSettings/api/revokeCancelation'
import { RevokeCancellationConfirmation } from '../Modals/RevokeCancellationConfirmation'
import { GLOBAL_MODALS } from 'Types/GlobalModal'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useCancelProduct } from 'Features/accountSettings/api/cancelProduct'
import { ConfirmationProductCancelModal } from '../Modals/ConfirmationProductCancelModal'
import { useIsTrialSubscription } from 'Hooks/trial/useIsTrialSubscription'

export function CancelPlan() {
  const { modalToShow, setModalToShow } = useAppStore()
  const { t } = useTranslation('accountSettings')
  const { data: plansDetails } = useGetPlansDetails()
  const { mutate, isLoading, isError, error } = useCancelPlan()
  const isTrialSubscription = useIsTrialSubscription()
  const cancelProduct = useCancelProduct()
  const revokeCancelation = useRevokeCancelation()
  const extendTrail = useExtendTrial()
  const { getErrorText } = useGetErrorText()

  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false)
  const [revokeCancelationModal, setRevokeCancelationModal] = useState<boolean>(false)
  const [productCancelConfirmationModal, setProductCancelConfirmationModal] =
    useState<boolean>(false)

  const isPlanCanceled = !!plansDetails?.cancelled

  const showExtendTrialModal = isTrialSubscription && !plansDetails?.trial?.extendRequest

  const onClickCancel = () =>
    showExtendTrialModal ? setModalToShow(GLOBAL_MODALS.EXTEND_TRIAL) : setIsCancelModalOpen(true)

  const handleClick = () => (isPlanCanceled ? setRevokeCancelationModal(true) : onClickCancel())

  const ctaTitleText = isPlanCanceled
    ? t('plan_details.cancelled_plan')
    : t('plan_details.cancel_plan')

  const ctaDescriptionText = isPlanCanceled ? (
    <Trans
      t={t}
      i18nKey={`plan_details.cancelled_plan_description`}
      values={{ date: format(new Date(plansDetails?.cancellationDate || 0), 'MMM dd yyyy') }}
    />
  ) : (
    t('plan_details.request_to_cancel')
  )

  const onExtendTrial = () => {
    extendTrail.mutate(undefined, {
      onSuccess: () => {
        setTimeout(() => {
          void queryClient.invalidateQueries({ queryKey: ['getPlansDetails'] })
          setModalToShow('')
        }, 1000)
      },
    })
  }

  const onRejectExtendTrial = () => {
    setModalToShow('')
    setIsCancelModalOpen(true)
  }

  const onCancelPlan = ({ reason, description }: CancelPlanRequestProps) => {
    mutate(
      { reason, description },
      {
        onSuccess: () => {
          setIsCancelModalOpen(false)
          setIsConfirmationModalOpen(true)
        },
      }
    )
  }

  return (
    <>
      <FlexRow style={{ marginTop: '28px' }}>
        <EditBoard />
        <ActionText onClick={handleClick}>{ctaTitleText}</ActionText>
      </FlexRow>

      <ActionDescription>{ctaDescriptionText}</ActionDescription>

      {isError && <ErrorText>{getErrorText(error)}</ErrorText>}

      {isCancelModalOpen && (
        <CancelPlanModal
          isOpen
          loading={isLoading}
          setIsOpen={setIsCancelModalOpen}
          onConfirm={onCancelPlan}
        />
      )}

      {revokeCancelationModal && (
        <RevokeCancellationConfirmation
          isOpen
          isLoading={revokeCancelation.isLoading}
          isSuccess={!!revokeCancelation.isSuccess}
          setIsOpen={setRevokeCancelationModal}
          onRevokeCancellation={revokeCancelation.mutate}
        />
      )}

      {isPlanCanceled && isConfirmationModalOpen && (
        <ConfirmationPlanCancelModal
          isOpen
          isTrailUser={isTrialSubscription}
          setIsOpen={setIsConfirmationModalOpen}
        />
      )}

      {modalToShow === GLOBAL_MODALS.EXTEND_TRIAL && (
        <ExtendTrialModal
          isOpen
          setIsOpen={() => setModalToShow('')}
          onConfirm={onExtendTrial}
          onCancelAccount={onRejectExtendTrial}
          loading={extendTrail.isLoading || cancelProduct.isLoading}
        />
      )}

      {productCancelConfirmationModal && (
        <ConfirmationProductCancelModal isOpen setIsOpen={setProductCancelConfirmationModal} />
      )}
    </>
  )
}
